"use client";

import { slugify } from "@repo/common";
import { format } from "date-fns";
import Image from "next/image";
import Link from "next/link";

import { cn } from "@/lib/utils";

interface CaseStudy {
  slug: string;
  title: string;
  description: string;
  company: string;
  companyLogoUrl?: string;
  publishedAt: string;
  industry?: string;
}

interface CaseStudiesListProps {
  caseStudies: CaseStudy[];
  industries: string[];
  currentIndustry?: string;
}

export default function CaseStudiesList({
  caseStudies,
  industries,
  currentIndustry,
}: CaseStudiesListProps) {
  return (
    <div className="grid gap-8 lg:grid-cols-[180px_1fr]">
      {/* Sidebar */}
      <nav className="space-y-1 lg:sticky lg:top-24 lg:self-start">
        <Link
          href="/customers"
          className={cn(
            "block text-sm transition-colors",
            !currentIndustry
              ? "text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          All Stories
        </Link>
        {industries.map((industry) => (
          <Link
            key={industry}
            href={`/customers/industry/${slugify(industry)}`}
            className={cn(
              "block text-sm transition-colors",
              currentIndustry?.toLowerCase() === industry.toLowerCase()
                ? "text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {industry}
          </Link>
        ))}
      </nav>

      {/* Case studies grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {caseStudies.map((study) => (
          <Link
            key={study.slug}
            href={`/customers/${study.slug}`}
            className="group border-border bg-card flex flex-col overflow-hidden rounded-lg border transition-transform duration-300 ease-in-out hover:-translate-y-1"
          >
            {/* Image Section */}
            <div className="relative aspect-video w-full overflow-hidden">
              {study.companyLogoUrl ? (
                <Image
                  src={study.companyLogoUrl}
                  alt={study.title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="bg-muted flex h-full w-full items-center justify-center">
                  <div className="text-center">
                    <svg
                      className="text-muted-foreground mx-auto size-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <p className="text-muted-foreground mt-2 text-sm">
                      {study.company}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Content Section */}
            <div className="flex grow flex-col p-6">
              <p className="text-muted-foreground grow text-sm leading-relaxed">
                {study.description}
              </p>
            </div>

            {/* Company/Date Section */}
            <div className="border-border bg-muted/50 border-t p-4">
              <div className="flex items-center justify-between">
                <p className="text-foreground text-sm font-medium">
                  {study.company}
                </p>
                <p className="text-muted-foreground text-xs">
                  {format(new Date(study.publishedAt), "MMM d, yyyy")}
                </p>
              </div>
              {study.industry && (
                <p className="text-muted-foreground mt-1 text-xs">
                  {study.industry}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
